import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { LayoutDashboard, Inbox, Palette, PanelTop, ListOrdered, SidebarOpen, Image, LayoutGrid, Megaphone, Gamepad2, Trophy, MessageSquare, AlignVerticalJustifyEnd, X, Package, Repeat, Settings as SettingsIcon, Briefcase, Globe, Wallet, Send, Users as UsersIcon, Shield, CreditCard, Tag, Bot, MessageCircle, Truck, Star, Eye, Scale, Coins, Sparkles } from 'lucide-react';
import { useSite } from '../../context/SiteContext';
import { resolveMediaUrl } from './MediaUpload';

const items = [
  { key: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { key: 'enquiries', label: 'Enquiries & Leads', icon: Inbox },
  { key: 'users', label: 'Registered Users', icon: UsersIcon },
  { key: 'chat', label: 'Live Chat', icon: MessageCircle },
  { key: 'reviews', label: 'Reviews & Ratings', icon: Star },
  { key: 'staff', label: 'Staff & Permissions', icon: Shield },
  { key: 'branding', label: 'Branding & Colors', icon: Palette },
  { key: 'header', label: 'Header', icon: PanelTop },
  { key: 'navigation', label: 'Top Navigation', icon: ListOrdered },
  { key: 'sidebar', label: 'Control Panel', icon: SidebarOpen },
  { key: 'hero', label: 'Hero Slides', icon: Image },
  { key: 'marquee', label: 'Logo Marquee & Social', icon: Repeat },
  { key: 'gamecards', label: 'Service Cards', icon: LayoutGrid },
  { key: 'products', label: 'Products (B2C / B2B)', icon: Package },
  { key: 'exchanges', label: 'Exchange Listings', icon: Globe },
  { key: 'dealingterms', label: 'Dealing Terms', icon: Scale },
  { key: 'payments', label: 'Payments We Accept', icon: Coins },
  { key: 'visibility', label: 'Section Visibility', icon: Eye },
  { key: 'promotions', label: 'Promotions', icon: Megaphone },
  { key: 'flyerad', label: 'Flyer Ad Popup', icon: Sparkles },
  { key: 'originals', label: 'Platforms Showcase', icon: Gamepad2 },
  { key: 'inplay', label: 'GET ID Platforms', icon: Trophy },
  { key: 'modals', label: 'Login / Signup', icon: MessageSquare },
  { key: 'footer', label: 'Footer', icon: AlignVerticalJustifyEnd },
  { key: 'payment', label: 'Payment Methods', icon: CreditCard },
  { key: 'aiconfig', label: 'AI Chat Bot', icon: Bot },
  { key: 'botconfig', label: 'OTP Setup', icon: Send },
  { key: 'botsetting', label: 'Bot Setting', icon: Bot },
  { key: 'settings', label: 'Settings', icon: SettingsIcon }
];

function SidebarContent({ active, onChange, isSuper, staffPerms, onItemClick }) {
  const navigate = useNavigate();
  const { data } = useSite();
  const brand = data.branding || {};
  // Brand label shown in the sidebar header — prefer admin-set siteName, then
  // header copy fallback, else hostname. NEVER hard-code "GAMEX HUB".
  const brandLabel = (brand.siteName && brand.siteName.trim())
    || (data.header?.brandText && data.header.brandText.trim())
    || (typeof window !== 'undefined' && window.location.hostname.split('.')[0])
    || 'Admin';
  const handle = (key) => {
    // Fire navigation synchronously, then close the drawer on the next tick so
    // React commits the state change BEFORE the Sheet's exit animation runs.
    // This was the source of the "click does nothing on mobile" bug.
    onChange(key);
    if (onItemClick) setTimeout(onItemClick, 0);
  };
  return (
    <div className="h-full flex flex-col">
      <div className="px-5 py-5 border-b border-zinc-800">
        <div className="flex items-center gap-2">
          {brand.logoUrl ? (
            <img src={resolveMediaUrl(brand.logoUrl)} alt="logo" className={`w-8 h-8 object-cover ${brand.logoShape === 'square' ? 'rounded-none' : brand.logoShape === 'rounded' ? 'rounded-md' : 'rounded-full'}`} />
          ) : (
            <span className="w-8 h-8 rounded-full bg-[color:var(--gold)] text-black font-extrabold flex items-center justify-center">G</span>
          )}
          <span className="font-impact text-lg text-[color:var(--gold)] uppercase truncate" data-testid="admin-brand-label">{brandLabel}</span>
        </div>
      </div>
      <nav className="flex-1 overflow-y-auto py-3">
        <div className="px-5 py-1 text-[10px] uppercase tracking-[0.18em] text-zinc-400 font-bold">Site</div>
        {items.map((it) => {
          if (staffPerms && !staffPerms.includes(it.key) && it.key !== 'dashboard') return null;
          const Icon = it.icon;
          const isActive = active === it.key;
          return (
            <button
              type="button"
              key={it.key}
              onClick={() => handle(it.key)}
              data-testid={`sidebar-${it.key}`}
              className={`group w-full flex items-center gap-3 px-5 py-3 text-sm font-semibold transition-all relative ${isActive ? 'text-[color:var(--gold)] bg-[color:var(--gold)]/10' : 'text-zinc-300 hover:text-white hover:bg-zinc-900/80'}`}
              style={{ touchAction: 'manipulation' }}
            >
              {isActive && <span className="absolute left-0 top-1 bottom-1 w-1 rounded-r-full bg-[color:var(--gold)]" />}
              <Icon size={17} className={isActive ? '' : 'text-zinc-500 group-hover:text-zinc-200'} />
              <span>{it.label}</span>
            </button>
          );
        })}
      </nav>
      <div className="px-5 py-4 border-t border-zinc-800 text-[11px] text-zinc-500">v2.0 · Admin CMS</div>
    </div>
  );
}

export default function AdminSidebar({ active, onChange, mobileOpen, onMobileClose, isSuper, staffPerms }) {
  // Lock body scroll while the mobile drawer is open
  useEffect(() => {
    if (!mobileOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [mobileOpen]);

  return (
    <>
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex fixed inset-y-0 left-0 w-72 bg-zinc-950 border-r border-zinc-800 z-30">
        <SidebarContent active={active} onChange={onChange} isSuper={isSuper} staffPerms={staffPerms} />
      </aside>

      {/* Mobile drawer — custom (no Radix Sheet) for bulletproof tap handling */}
      <div
        className={`lg:hidden fixed inset-0 z-50 ${mobileOpen ? '' : 'pointer-events-none'}`}
        data-testid="admin-mobile-drawer"
        aria-hidden={!mobileOpen}
      >
        {/* Backdrop */}
        <div
          onClick={onMobileClose}
          className={`absolute inset-0 bg-black transition-opacity duration-200 ${mobileOpen ? 'opacity-60' : 'opacity-0'}`}
        />
        {/* Drawer panel */}
        <div
          className={`absolute inset-y-0 left-0 w-[280px] sm:w-[300px] bg-zinc-950 border-r border-zinc-800 shadow-2xl transition-transform duration-200 ease-out ${mobileOpen ? 'translate-x-0' : '-translate-x-full'}`}
          onClick={(e) => e.stopPropagation()}
        >
          <button
            type="button"
            onClick={onMobileClose}
            className="absolute right-3 top-3 z-10 p-1.5 rounded-md bg-zinc-900 text-zinc-300 hover:text-white"
            data-testid="admin-mobile-close-btn"
            aria-label="Close menu"
            style={{ touchAction: 'manipulation' }}
          >
            <X size={16} />
          </button>
          <SidebarContent active={active} onChange={onChange} isSuper={isSuper} staffPerms={staffPerms} onItemClick={onMobileClose} />
        </div>
      </div>
    </>
  );
}
